import { css } from '@emotion/react';
import {
  faEllipsisVertical,
  faEye,
  faEyeSlash,
  faMagnifyingGlassLocation,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type Ref,
} from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import {
  ContextMenuComponent, ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { LabelOrMarkerVisualizerContainer } from '~/_shared/labelOrMarkerVisualizer/labelOrMarkerVisualizer.container';
import type { Theme } from '~/_shared/themes/theme.model';
import type { QuadrilateralDimensions } from '~/_shared/types/coordinateSystem/coordinateSystem';
import type { MarkerSettings } from '~/_shared/types/markers/visualSettings.types';
import { useTranslation } from '~/_shared/utils/hooks';
import type { PlaceSearchListingItem } from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.container';
import { PLACES_MARKER_DIMENSIONS } from './placesTool.helpers';

export type PlaceContextMenuProps = Readonly<{
  isContextMenuVisible: boolean;
  onHideContextMenu: () => void;
  onOpenContextMenu: () => void;
}>;

const wrapperStyle = css({
  padding: '4px 0',
  display: 'flex',
  gap: 2,
  flexDirection: 'row',
});

const titleStyle = css({
  fontSize: 16,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const descriptionAndActionsStyle = css({
  flexGrow: 1,
  flexBasis: 0,
  minWidth: 0,
});

const totalStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
});

const loaderStyle = css({
  background: 'none',
});

const distanceAndActionsStyle = css({
  alignItems: 'top',
  display: 'flex',
  fontSize: 12,
  justifyContent: 'space-between',
});

const distanceStyle = css({
  textTransform: 'uppercase',
});

const buttonsContainerStyle = css({
  display: 'flex',
  gap: 4,
});

const markerVisualizerContainerStyle = css({
  width: PLACES_MARKER_DIMENSIONS.width,
  position: 'relative',
});

type PlacesListingHeaderProps = Readonly<{
  markerVisualizerProps: Readonly<{
    markerSettings: MarkerSettings;
    maxDimensions: QuadrilateralDimensions;
    maxSize: number;
  }>;
  contextMenuProps: PlaceContextMenuProps;
  place: PlaceSearchListingItem;
}>;

export const PlacesListingHeaderComponent: FC<PlacesListingHeaderProps> = ({
  markerVisualizerProps,
  contextMenuProps: {
    isContextMenuVisible: isPlaceContextMenuVisible,
    onHideContextMenu: onHidePlaceContextMenu,
    onOpenContextMenu: onOpenPlaceContextMenu,
  },
  place,
}) => {
  const { displayName, distance, items, onRemove, isVisible, onVisibilityToggle, isLoading, onZoomToArea } = place;

  const [t] = useTranslation();

  return (
    <div css={wrapperStyle}>
      <div css={markerVisualizerContainerStyle}>
        {isLoading && <OverlayLoaderComponent css={loaderStyle} loaderSize={PLACES_MARKER_DIMENSIONS.height} />}
        {!isLoading && (
          <LabelOrMarkerVisualizerContainer
            {...markerVisualizerProps}
          />
        )}
      </div>
      <div css={descriptionAndActionsStyle}>
        <div css={titleStyle} title={displayName}>
          {displayName}
        </div>

        <div css={totalStyle}>
          {isLoading ? t('Loading...') : t('placesTool.locationsTotal', { total: items.length })}
        </div>

        <div css={distanceAndActionsStyle}>
          <span css={distanceStyle}>{`${distance.value} ${t(distance.unit)}`}</span>
          <div css={buttonsContainerStyle}>
            <RoundButtonComponent
              icon={isVisible ? faEye : faEyeSlash}
              onClick={onVisibilityToggle}
              size={RoundButtonSize.Small}
            />
            <ContextMenuComponent
              isFixed
              maxWidth={280}
              preferredPlacement="bottom-start"
              isVisible={isPlaceContextMenuVisible}
              onHide={onHidePlaceContextMenu}
              renderMenuContent={() => (
                <>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      icon={faMagnifyingGlassLocation}
                      onClick={onZoomToArea}
                    >
                      {t('Zoom')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                  <ContextMenuItemCoreComponent>
                    <ContextMenuDefaultItem
                      icon={faTrash}
                      onClick={onRemove}
                      isDestructive
                    >
                      {t('Delete')}
                    </ContextMenuDefaultItem>
                  </ContextMenuItemCoreComponent>
                </>
              )}
            >
              {ref => (
                <RoundButtonComponent
                  ref={ref as Ref<HTMLButtonElement>}
                  icon={faEllipsisVertical}
                  onClick={onOpenPlaceContextMenu}
                  size={RoundButtonSize.Small}
                />
              )}
            </ContextMenuComponent>
          </div>
        </div>
      </div>
    </div>
  );
};
